"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronLeft, MoreHorizontal } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { AddFileDialog } from "@/components/command-skills/add-file-dialog";
import { EditSkillDialog } from "@/components/command-skills/edit-skill-dialog";
import { ShareSkillDialog } from "@/components/command-skills/share-skill-dialog";
import { SkillBadges } from "@/components/command-skills/skill-badges";
import { SkillConfirmDialog } from "@/components/command-skills/skill-confirm-dialog";
import { SkillFileTree } from "@/components/command-skills/skill-file-tree";
import { SkillFileViewer } from "@/components/command-skills/skill-file-viewer";
import { SkillSwitch } from "@/components/command-skills/skill-switch";
import {
  downloadSkillArchive,
  isSkillMissing,
  readSkillError,
} from "@/components/command-skills/skill-api";
import { formatSkillUpdated, ownerLabel, skillMetaLine, withSkillReadLine } from "@/components/command-skills/skill-utils";
import { UploadSkillDialog, skillUploadErrorMessage } from "@/components/command-skills/upload-skill-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  removeSkillFromCaches,
  useDeleteSkill,
  useDeleteSkillFile,
  useSkillDetail,
  useSkillVisibility,
  useToggleSkill,
  useUpdateSkill,
  useUploadSkillFile,
  useUploadSkillVersion,
} from "@/hooks/use-command-skills";
import { usePermissions } from "@/context/permissions-context";
import { useSession } from "@/hooks/use-session";
import type { SkillDetail } from "@/types/command-skill";

interface SkillDetailViewProps {
  skillId: string;
  onBack: () => void;
  showCreationHint?: boolean;
  /** Side panel (chat). Leaves room for the sheet close button and uses the panel width. */
  embedded?: boolean;
}

type ConfirmKind =
  | null
  | { type: "publish" }
  | { type: "unpublish" }
  | { type: "delete-skill" }
  | { type: "delete-file"; path: string };

export function SkillDetailView({
  skillId,
  onBack,
  showCreationHint = false,
  embedded = false,
}: SkillDetailViewProps) {
  const queryClient = useQueryClient();
  const detail = useSkillDetail(skillId);
  const { hasPermission } = usePermissions();
  const canToggle = hasPermission("TOGGLE_COMMAND_SKILL");
  const { data: session } = useSession();
  const isSuperAdmin = Boolean(session) && !session?.organization?.id;

  const [selectedPath, setSelectedPath] = useState("SKILL.md");
  const [panel, setPanel] = useState<"overview" | "contents">("overview");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [versionOpen, setVersionOpen] = useState(false);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [fileDialog, setFileDialog] = useState<null | { mode: "add" } | { mode: "replace"; path: string }>(
    null,
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmKind>(null);
  const [downloading, setDownloading] = useState(false);
  const gone = useRef(false);

  const updateSkill = useUpdateSkill(skillId);
  const uploadVersion = useUploadSkillVersion(skillId);
  const uploadFile = useUploadSkillFile(skillId);
  const deleteFile = useDeleteSkillFile(skillId);
  const visibility = useSkillVisibility(skillId);
  const toggle = useToggleSkill();
  const remove = useDeleteSkill(skillId);

  const skill = detail.data;

  useEffect(() => {
    if (!detail.error || gone.current) return;
    if (!isSkillMissing(detail.error)) return;
    gone.current = true;
    removeSkillFromCaches(queryClient, skillId);
    toast.error("This skill is no longer available");
    onBack();
  }, [detail.error, onBack, queryClient, skillId]);

  useEffect(() => {
    if (!skill) return;
    if (!skill.files.some((file) => file.path === selectedPath)) {
      setSelectedPath("SKILL.md");
    }
  }, [selectedPath, skill]);

  const handleMissing = () => {
    if (gone.current) return;
    gone.current = true;
    removeSkillFromCaches(queryClient, skillId);
    toast.error("This skill is no longer available");
    onBack();
  };

  const runVisibility = (next: "PERSONAL" | "TEAM") => {
    if (!skill) return;
    visibility.mutate(next, {
      onSuccess: () => setConfirm(null),
      onError: (error) => {
        if (isSkillMissing(error)) {
          setConfirm(null);
          handleMissing();
          return;
        }
        const parsed = readSkillError(error, "Could not update visibility");
        if (parsed.code === "COMMAND_TEAM__SKILL_NAME_CONFLICT") {
          toast.error(
            skill.is_global
              ? parsed.message
              : `Your organization already has a published skill named ${skill.name}`,
          );
        } else {
          toast.error(parsed.message);
        }
        setConfirm(null);
      },
    });
  };

  if (detail.isLoading) {
    return <div className="px-6 py-10 text-sm text-muted-foreground">Loading skill…</div>;
  }

  if (!skill) {
    if (detail.error && !isSkillMissing(detail.error)) {
      return (
        <div className="px-6 py-10">
          <p className="text-sm text-destructive">{readSkillError(detail.error).message}</p>
          <Button type="button" variant="outline" className="mt-4" onClick={onBack}>
            Back
          </Button>
        </div>
      );
    }
    return null;
  }

  const publishLabel = skill.is_global ? "Go live" : "Publish to organization";
  const unpublishLabel = skill.is_global ? "Move to draft" : "Unpublish";

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
      <div
        className={
          embedded
            ? "flex w-full shrink-0 flex-col px-6 pb-0 pr-16 pt-5"
            : "mx-auto flex w-full max-w-5xl shrink-0 flex-col px-4 pb-3 pt-4 sm:px-6 sm:pb-4 sm:pt-6"
        }
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {embedded ? null : (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
                aria-label="Back to skills"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
              <h1 className="break-words text-lg font-medium tracking-[-0.02em] sm:text-xl">{skill.name}</h1>
              <SkillBadges skill={skill} isSuperAdmin={isSuperAdmin} />
            </div>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {skillMetaLine(skill)} · Updated {formatSkillUpdated(skill.updated_at)}
            </p>
            </div>
          </div>
          <div className={embedded ? "flex shrink-0 items-center gap-2" : "flex shrink-0 items-center gap-2 pl-11 sm:pl-0"}>
            {canToggle ? (
              <SkillSwitch
                checked={skill.enabled}
                label={`${skill.enabled ? "Turn off" : "Turn on"} ${skill.name}`}
                onCheckedChange={(enabled) =>
                  toggle.mutate(
                    { id: skill.id, enabled },
                    {
                      onError: (error) => {
                        if (isSkillMissing(error)) onBack();
                      },
                    },
                  )
                }
              />
            ) : null}
            {skill.permissions.can_share ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 rounded-lg border-border px-3 font-medium shadow-none hover:border-border hover:bg-muted hover:text-foreground"
                onClick={() => setShareOpen(true)}
              >
                Share
              </Button>
            ) : null}
            <SkillActionsMenu
              skill={skill}
              downloading={downloading}
              onEdit={() => setEditOpen(true)}
              onPublish={() => setConfirm({ type: "publish" })}
              onUnpublish={() => setConfirm({ type: "unpublish" })}
              onUploadVersion={() => {
                setVersionError(null);
                setVersionOpen(true);
              }}
              onDownload={() => {
                setDownloading(true);
                void downloadSkillArchive(skill.id, skill.name)
                  .catch((error) => {
                    if (isSkillMissing(error)) {
                      handleMissing();
                      return;
                    }
                    toast.error(readSkillError(error, "Could not download this skill").message);
                  })
                  .finally(() => setDownloading(false));
              }}
              onDelete={() => setConfirm({ type: "delete-skill" })}
            />
          </div>
        </div>
        {!skill.is_owner && canToggle ? (
          <p className="mt-4 text-[13px] leading-5 text-muted-foreground">
            You can turn this skill on or off for yourself. Only {ownerLabel(skill)} can change it.
          </p>
        ) : null}
        <div className="mt-4 flex items-center gap-5 border-b border-border">
          <button
            type="button"
            onClick={() => setPanel("overview")}
            className={
              panel === "overview"
                ? "-mb-px border-b-2 border-foreground pb-2 text-sm font-medium text-foreground"
                : "-mb-px border-b-2 border-transparent pb-2 text-sm text-muted-foreground hover:text-foreground"
            }
          >
            Overview
          </button>
          <button
            type="button"
            onClick={() => setPanel("contents")}
            className={
              panel === "contents"
                ? "-mb-px border-b-2 border-foreground pb-2 text-sm font-medium text-foreground"
                : "-mb-px border-b-2 border-transparent pb-2 text-sm text-muted-foreground hover:text-foreground"
            }
          >
            Contents
            <span className="ml-1.5 tabular-nums text-muted-foreground">{skill.files.length}</span>
          </button>
        </div>
      </div>

      {panel === "overview" ? (
        <div className={embedded ? "min-h-0 w-full flex-1 overflow-y-auto px-6 pb-8" : "mx-auto min-h-0 w-full max-w-5xl flex-1 overflow-y-auto px-4 pb-8 sm:px-6"}>
          <h2 className="mt-6 text-sm font-medium">Description</h2>
          <p className="mt-2 max-w-3xl text-[15px] leading-6 text-muted-foreground">{skill.description}</p>
          {showCreationHint && skill.permissions.can_edit ? (
            <p className="mt-4 text-[13px] leading-5 text-muted-foreground">
              Add reference docs or scripts with <span className="font-medium text-foreground">Add file</span> on Contents.
            </p>
          ) : null}
        </div>
      ) : (
      <div className={embedded ? "flex min-h-0 w-full flex-1 flex-col px-6 pb-6 pt-4" : "mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col px-4 pb-4 pt-4 sm:px-6 sm:pb-6"}>
      <div className="grid min-h-0 w-full flex-1 grid-cols-1 grid-rows-[minmax(0,10rem)_minmax(0,1fr)] overflow-hidden rounded-2xl border border-border md:grid-cols-[13.5rem_minmax(0,1fr)] md:grid-rows-1">
        <div className="min-h-0 overflow-y-auto border-b border-border bg-muted/30 md:border-b-0 md:border-r">
          <SkillFileTree
            files={skill.files}
            selectedPath={selectedPath}
            canEdit={skill.permissions.can_edit}
            collapsed={collapsed}
            onToggleFolder={(path) =>
              setCollapsed((current) => ({ ...current, [path]: !current[path] }))
            }
            onSelect={setSelectedPath}
            onAddFile={() => {
              setFileError(null);
              setFileDialog({ mode: "add" });
            }}
            onReplace={(path) => {
              setFileError(null);
              setFileDialog({ mode: "replace", path });
            }}
            onDelete={(path) => setConfirm({ type: "delete-file", path })}
          />
        </div>
        <div className="min-h-0 min-w-0 overflow-auto">
          <SkillFileViewer
            skill={skill}
            path={selectedPath}
            saving={updateSkill.isPending || uploadFile.isPending}
            onSaveInstructions={async (instructions) => {
              await updateSkill.mutateAsync({ instructions });
            }}
            onSaveFile={async (filePath, file) => {
              await uploadFile.mutateAsync({ path: filePath, file });
            }}
          />
        </div>
      </div>
      </div>
      )}

      <EditSkillDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        skill={skill}
        pending={updateSkill.isPending}
        onSubmit={(body) => {
          if (!body.description && !body.instructions) {
            setEditOpen(false);
            return;
          }
          updateSkill.mutate(body, {
            onSuccess: () => setEditOpen(false),
            onError: (error) => {
              if (isSkillMissing(error)) onBack();
            },
          });
        }}
      />

      <ShareSkillDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        skill={skill}
        onMissing={handleMissing}
        onPublishRequest={() => setConfirm({ type: "publish" })}
        onUnpublishRequest={() => setConfirm({ type: "unpublish" })}
      />

      <UploadSkillDialog
        open={versionOpen}
        onOpenChange={setVersionOpen}
        mode="version"
        pending={uploadVersion.isPending}
        error={versionError}
        onSubmit={(file, onProgress) => {
          setVersionError(null);
          uploadVersion.mutate(
            { file, onProgress },
            {
              onSuccess: () => setVersionOpen(false),
              onError: (error) => {
                if (isSkillMissing(error)) {
                  setVersionOpen(false);
                  handleMissing();
                  return;
                }
                setVersionError(skillUploadErrorMessage(error));
              },
            },
          );
        }}
      />

      <AddFileDialog
        open={fileDialog !== null}
        onOpenChange={(next) => {
          if (!next) setFileDialog(null);
        }}
        mode={fileDialog?.mode ?? "add"}
        fixedPath={fileDialog?.mode === "replace" ? fileDialog.path : undefined}
        existing={skill.files}
        pending={uploadFile.isPending}
        error={fileError}
        onSubmit={(path, file, onProgress) => {
          setFileError(null);
          uploadFile.mutate(
            { path, file, onProgress },
            {
              onSuccess: (next) => {
                const nextInstructions = withSkillReadLine(next.instructions ?? skill?.instructions ?? "", path);
                const current = next.instructions ?? skill?.instructions ?? "";
                if (nextInstructions !== current) {
                  updateSkill.mutate(
                    { instructions: nextInstructions },
                    {
                      onError: () => toast.error("File added, but SKILL.md was not updated"),
                    },
                  );
                }
                setFileDialog(null);
                if (next.files.some((item) => item.path === path)) setSelectedPath(path);
              },
              onError: (error) => {
                if (isSkillMissing(error)) {
                  setFileDialog(null);
                  handleMissing();
                  return;
                }
                setFileError(readSkillError(error, "Could not save this file").message);
              },
            },
          );
        }}
      />

      <SkillConfirmDialog
        open={confirm?.type === "publish"}
        onOpenChange={(next) => !next && setConfirm(null)}
        title={publishLabel}
        description={
          skill.is_global
            ? "This skill will be available to every organization."
            : "Everyone in your organization will see this skill, and it will be ON for them by default."
        }
        confirmLabel={publishLabel}
        pending={visibility.isPending}
        onConfirm={() => runVisibility("TEAM")}
      />
      <SkillConfirmDialog
        open={confirm?.type === "unpublish"}
        onOpenChange={(next) => !next && setConfirm(null)}
        title={unpublishLabel}
        description="Members will no longer see this skill."
        confirmLabel={unpublishLabel}
        pending={visibility.isPending}
        onConfirm={() => runVisibility("PERSONAL")}
      />
      <SkillConfirmDialog
        open={confirm?.type === "delete-skill"}
        onOpenChange={(next) => !next && setConfirm(null)}
        title={`Delete ${skill.name}?`}
        description="This can't be undone and removes it for everyone."
        confirmLabel="Delete"
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          remove.mutate(undefined, {
            onSuccess: () => {
              toast.success(`Deleted ${skill.name}`);
              onBack();
            },
            onError: (error) => {
              if (isSkillMissing(error)) {
                handleMissing();
                return;
              }
              toast.error(readSkillError(error, "Could not delete this skill").message);
              setConfirm(null);
            },
          });
        }}
      />
      <SkillConfirmDialog
        open={confirm?.type === "delete-file"}
        onOpenChange={(next) => !next && setConfirm(null)}
        title="Delete file?"
        description={
          confirm?.type === "delete-file"
            ? `Delete ${confirm.path}? This removes it from the skill.`
            : ""
        }
        confirmLabel="Delete"
        destructive
        pending={deleteFile.isPending}
        onConfirm={() => {
          if (confirm?.type !== "delete-file") return;
          const path = confirm.path;
          deleteFile.mutate(path, {
            onSuccess: () => {
              setConfirm(null);
              if (selectedPath === path) setSelectedPath("SKILL.md");
            },
            onError: (error) => {
              if (isSkillMissing(error)) {
                setConfirm(null);
                handleMissing();
                return;
              }
              const parsed = readSkillError(error, "Could not delete this file");
              toast.error(parsed.message);
              if (parsed.code === "COMMAND_TEAM__SKILL_FILE_NOT_FOUND") {
                void queryClient.invalidateQueries({ queryKey: ["skill", skillId] });
              }
              setConfirm(null);
            },
          });
        }}
      />
    </div>
  );
}

function SkillActionsMenu({
  skill,
  downloading,
  onEdit,
  onPublish,
  onUnpublish,
  onUploadVersion,
  onDownload,
  onDelete,
}: {
  skill: SkillDetail;
  downloading: boolean;
  onEdit: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onUploadVersion: () => void;
  onDownload: () => void;
  onDelete: () => void;
}) {
  const { permissions, is_global: isGlobal } = skill;
  const itemClass = "focus:bg-iai-purple-4 focus:text-iai-navy";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label="Skill actions"
          className="size-8 rounded-lg border-border shadow-none hover:border-border hover:bg-muted hover:text-foreground"
        >
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {permissions.can_edit ? (
          <DropdownMenuItem className={itemClass} onClick={onEdit}>
            Edit details
          </DropdownMenuItem>
        ) : null}
        {permissions.can_publish && !isGlobal ? (
          <DropdownMenuItem className={itemClass} onClick={onPublish}>
            Publish to organization
          </DropdownMenuItem>
        ) : null}
        {permissions.can_publish && isGlobal ? (
          <DropdownMenuItem className={itemClass} onClick={onPublish}>
            Go live
          </DropdownMenuItem>
        ) : null}
        {permissions.can_unpublish ? (
          <DropdownMenuItem className={itemClass} onClick={onUnpublish}>
            {isGlobal ? "Move to draft" : "Unpublish"}
          </DropdownMenuItem>
        ) : null}
        {permissions.can_edit ? (
          <DropdownMenuItem className={itemClass} onClick={onUploadVersion}>
            Upload new version
          </DropdownMenuItem>
        ) : null}
        {permissions.can_edit || permissions.can_publish || permissions.can_unpublish ? (
          <DropdownMenuSeparator />
        ) : null}
        <DropdownMenuItem className={itemClass} disabled={downloading} onClick={onDownload}>
          {downloading ? "Downloading…" : "Download"}
        </DropdownMenuItem>
        {permissions.can_delete ? (
          <DropdownMenuItem
            className="text-destructive focus:bg-iai-purple-4 focus:text-destructive"
            onClick={onDelete}
          >
            Delete
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
